import React, { useEffect, useRef, useState } from "react";
import { keyframes } from "@emotion/react";
import styled from "@emotion/styled";

import UIcon from "../../../lib/icons/untitled_ui/UIcon";
import { fadeIn } from "../../../styles/animations";

export type ModalProps = {
  children: React.ReactNode;
  isOpen: boolean;
  modalClose: () => void;
  onOpen?: () => void;
  onClose?: () => void;
  maxWidth?: string;
  showCloseButton?: boolean;
  backgroundClickClose?: boolean;
  noContentBackground?: boolean;
};

/**
 * @param children - The content of the modal
 * @param isOpen - Whether the modal is open or not
 * @param modalClose - Function which closes the modal - onClose is called when isOpen is set to false
 * @param onOpen - Function which runs when modal is opened (at beginning of animation)
 * @param onClose - Function which runs when modal is closed (at beginning of animation)
 * @param maxWidth - Max width of the modal
 * @param showCloseButton - Default true. Whether to show a close button in the top right corner (content if noContentBackground is false, page if true).
 * @param backgroundClickClose - Default true. Whether if the modal should close when clicking on the background of the page (outside the modal content)
 * @param noContentBackground - Default false. Whether the modal content should have a background. Set to true if you need something very custom (e.g. video player)
 */
const Modal = ({
  children,
  isOpen,
  modalClose,
  onOpen,
  onClose,
  maxWidth,
  showCloseButton = true,
  backgroundClickClose = true,
  noContentBackground = false,
}: ModalProps) => {
  const [isModalClosing, setIsModalClosing] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    let closeTimer: NodeJS.Timeout | undefined = undefined;
    if (isOpen) {
      clearTimeout(closeTimer); // Stop isModalClosing from being updated if mid-animation

      // While we do have data-closing controlled it only happens next render while .showModal() happens immediately
      // meaning there can be a flicker of the modal during the animation which looks odd. Therefore, setting the attribute
      // manually here so it happens before next render (which then manages it again)
      dialogRef.current?.setAttribute("data-closing", "false");
      setIsModalClosing(false);
      dialogRef.current?.showModal();

      onOpen?.();
    } else {
      // Same problem as above, but in reverse (when closing the modal)
      dialogRef.current?.setAttribute("data-closing", "true");
      setIsModalClosing(true);
      dialogRef.current?.close();

      onClose?.();
      closeTimer = setTimeout(() => {
        setIsModalClosing(false);
      }, 300); // Match the duration of the closing animation
    }

    return () => clearTimeout(closeTimer);
  }, [isOpen, onOpen, onClose]);

  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (backgroundClickClose && e.target === e.currentTarget) {
      modalClose();
    }
  };

  return (
    <Dialog
      onMouseDown={handleBackgroundClick} // Not using onClick so if user selects text and drags into the background area it won't trigger
      onClose={(e) => {
        e.stopPropagation(); // Allows multiple dialogs to be nested
        modalClose();
      }}
      ref={dialogRef}
      noContentBackground={noContentBackground}
      maxWidth={maxWidth}
      data-closing={isModalClosing ? "true" : "false"}
    >
      {isOpen || isModalClosing ? (
        <>
          <DialogWrapper noContentBackground={noContentBackground}>{children}</DialogWrapper>
          {showCloseButton && (
            <CloseModalButton
              onClick={modalClose}
              location={noContentBackground ? "page" : "modal"}
            >
              <UIcon icon="xClose" size={noContentBackground ? 32 : 16} color="inherit" />
            </CloseModalButton>
          )}
        </>
      ) : null}
    </Dialog>
  );
};

const fadeInBackground = keyframes({
  from: { opacity: 0 },
  to: { opacity: 0.5 },
});

const Dialog = styled.dialog<{ noContentBackground: boolean; maxWidth?: string }>(
  ({ theme, noContentBackground, maxWidth = "calc(100vw - 60px)" }) => ({
    // Disabling border + padding so when backgroundClickClose is true, it doesn't trigger from clicking on the border or padding
    borderWidth: 0,
    padding: 0,

    maxWidth,
    maxHeight: "calc(100vh - 26px)",
    minWidth: "300px",

    // Using both animation and transition, since animation runs when modal is opened while transition runs when modal is closed
    // Should be possible to only use one of them but I couldn't get it to work without side effects (namely dialog contents being visible inline on the page)
    animation: `${fadeIn} 0.3s ease-out`,
    transition:
      "display 300ms allow-discrete ease-out, overlay 300ms allow-discrete ease-out, opacity 300ms ease-out",
    opacity: 0,
    "&::backdrop": {
      animation: `${fadeInBackground} 300ms ease-out`,
      transition: "background-color 300ms ease-out, opacity 300ms ease-out",
      backgroundColor: "transparent",
      opacity: 0,
    },
    "&[open][data-closing=false]": {
      opacity: 1,
      "&::backdrop": {
        backgroundColor: theme.colors.sysBackgroundOverlay,
        opacity: 0.5,
      },
    },

    ...(noContentBackground
      ? {
          backgroundColor: "transparent",
        }
      : {
          borderRadius: "8px",
        }),
  }),
);

const DialogWrapper = styled.div<{ noContentBackground: boolean }>(({ noContentBackground }) => ({
  padding: noContentBackground ? undefined : "16px",
}));

const CloseModalButton = styled.button<{ location: "page" | "modal" }>(({ theme, location }) => ({
  position: location === "modal" ? "absolute" : "fixed",
  top: "0px",
  right: "0px",
  padding: "16px",
  color: theme.colors.sysTextSecondary,
  backgroundColor: "transparent",
  border: "none",
  lineHeight: 0,
  cursor: "pointer",
  zIndex: 2,
  "&:hover": {
    color: theme.colors.sysTextBlack,
  },
}));

export default Modal;
